import { __assign } from 'tslib';
import React, { forwardRef, useRef, useState, useEffect, useMemo } from 'react';
import mergeRefs from 'react-merge-refs';
import { useVirtual } from 'react-virtual';
import cn from 'classnames';
import { useMatchMedia } from '../../../../mq/esm';
import { Scrollbar } from '../../../../scrollbar/esm';
import { isClient } from '../../../../shared/esm';
import { SIZE_TO_CLASSNAME_MAP, DEFAULT_VISIBLE_OPTIONS } from '../../consts.js';
import { usePrevious, lastIndexOf, useVirtualVisibleOptions, isGroup } from '../../utils.js';
import { Optgroup } from '../optgroup/Component.js';

var styles = {"virtualOptionsList":"select__virtualOptionsList_1wwne","virtualOptionsListHeader":"select__virtualOptionsListHeader_1wwne","virtualOptionsListFooter":"select__virtualOptionsListFooter_1wwne","withBorder":"select__withBorder_1wwne","scrollable":"select__scrollable_1wwne","inner":"select__inner_1wwne","virtualRow":"select__virtualRow_1wwne","highlighted":"select__highlighted_1wwne","emptyPlaceholder":"select__emptyPlaceholder_1wwne","size-64":"select__size-64_1wwne","size-72":"select__size-72_1wwne"};
require('./index.css');

var VirtualOptionsList = forwardRef(function (_a, ref) {
    var _b;
    var _c = _a.size, size = _c === void 0 ? 48 : _c, _d = _a.flatOptions, flatOptions = _d === void 0 ? [] : _d, _e = _a.highlightedIndex, highlightedIndex = _e === void 0 ? -1 : _e, optionGroupClassName = _a.optionGroupClassName, className = _a.className, getOptionProps = _a.getOptionProps, _f = _a.groupOptionProps, groupOptionProps = _f === void 0 ? {} : _f, Option = _a.Option, open = _a.open, _g = _a.options, options = _g === void 0 ? [] : _g, _h = _a.Optgroup, Optgroup$1 = _h === void 0 ? Optgroup : _h, dataTestId = _a.dataTestId, emptyPlaceholder = _a.emptyPlaceholder, _j = _a.visibleOptions, visibleOptions = _j === void 0 ? DEFAULT_VISIBLE_OPTIONS : _j, header = _a.header, footer = _a.footer, _k = _a.showFooter, showFooter = _k === void 0 ? true : _k, optionsListWidth = _a.optionsListWidth, onScroll = _a.onScroll, nativeScrollbarProp = _a.nativeScrollbar, setHighlightedIndex = _a.setHighlightedIndex, selectedItems = _a.selectedItems, setSelectedItems = _a.setSelectedItems, search = _a.search, multiple = _a.multiple;
    var listRef = useRef(null);
    var parentRef = useRef(null);
    var scrollbarRef = useRef(null);
    var _l = useState(''), visibleOptionsInvalidateKey = _l[0], setVisibleOptionsInvalidateKey = _l[1];
    var prevHighlightedIndex = usePrevious(highlightedIndex) || -1;
    var query = '(max-width: 1023px)';
    var nativeScrollbar = useMatchMedia(query, function () {
        return isClient() ? window.matchMedia(query).matches : true;
    })[0];
    var rowVirtualizer = useVirtual({
        size: flatOptions.length,
        parentRef: (ref || parentRef),
        overscan: 15,
    });
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    nativeScrollbar = Boolean(nativeScrollbarProp !== null && nativeScrollbarProp !== void 0 ? nativeScrollbarProp : nativeScrollbar);
    // Сколл к выбранному пункту при открытии меню
    useEffect(function () {
        if (open) {
            rowVirtualizer.scrollToIndex(highlightedIndex, { align: 'end' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);
    // Скролл к пункту, которого нет на экране
    useEffect(function () {
        if (highlightedIndex === -1)
            return;
        if (!rowVirtualizer.virtualItems.some(function (option) { return option.index === highlightedIndex; })) {
            rowVirtualizer.scrollToIndex(highlightedIndex, { align: 'end' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [highlightedIndex]);
    // Циклическая навигация
    useEffect(function () {
        var notDisabled = function (option) { return !option.disabled; };
        var firstNonDisabled = flatOptions.findIndex(notDisabled);
        var lastNonDisabled = lastIndexOf(flatOptions, notDisabled);
        if (prevHighlightedIndex <= firstNonDisabled &&
            highlightedIndex === flatOptions.length - 1) {
            rowVirtualizer.scrollToIndex(lastNonDisabled);
        }
        if (prevHighlightedIndex >= lastNonDisabled && highlightedIndex === 0) {
            rowVirtualizer.scrollToIndex(0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [prevHighlightedIndex, highlightedIndex]);
    useEffect(function () {
        setVisibleOptionsInvalidateKey(
        /**
         * react-virtual может несколько раз отрендерить список с одним элементом,
         * поэтому нужно еще раз пересчитать высоту, когда список ВИДИМЫХ пунктов будет отрендерен полностью
         * Также, высоту нужно пересчитывать при изменении пунктов меню
         */
        rowVirtualizer.virtualItems
            .slice(0, Math.min(rowVirtualizer.virtualItems.length, visibleOptions + 1))
            .map(function (item) { return flatOptions[item.index].key; })
            .join('_'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [rowVirtualizer.virtualItems.length, flatOptions]);
    useVirtualVisibleOptions({
        visibleOptions: visibleOptions,
        invalidate: visibleOptionsInvalidateKey,
        listRef: listRef,
        styleTargetRef: nativeScrollbar ? parentRef : scrollbarRef,
        open: open,
    });
    // Т.к. рендерится плоский список, необходимо знать индекс, когда начинается новая группа
    var groupStartIndexes = useMemo(function () {
        var currentIndex = 0;
        return options.reduce(function (acc, option, index) {
            if (isGroup(option)) {
                acc[currentIndex] = index;
                currentIndex += option.options.length;
            }
            else {
                currentIndex += 1;
            }
            return acc;
        }, {});
    }, [options]);
    var renderList = function () {
        return rowVirtualizer.virtualItems.map(function (virtualRow) {
            var _a;
            var option = flatOptions[virtualRow.index];
            var renderGroup = function () {
                var group = options[groupStartIndexes[virtualRow.index]];
                if (!group)
                    return null;
                var groupSelectedItems = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.filter(function (_a) {
                    var selectedItemKey = _a.key;
                    return group.options.some(function (opt) { return opt.key === selectedItemKey; });
                });
                var handleSelectedItems = function (items) {
                    var _a;
                    setSelectedItems(((_a = selectedItems === null || selectedItems === void 0 ? void 0 : selectedItems.filter(function (_a) {
                        var selectedItemKey = _a.key;
                        return !group.options.some(function (opt) { return opt.key === selectedItemKey; });
                    })) !== null && _a !== void 0 ? _a : []).concat(items));
                };
                return (React.createElement(Optgroup$1, __assign({ label: group.label, size: size, className: optionGroupClassName, options: group.options, selectedItems: groupSelectedItems, setSelectedItems: handleSelectedItems, search: search, multiple: multiple }, groupOptionProps)));
            };
            return (React.createElement("div", { key: virtualRow.index, ref: virtualRow.measureRef, className: cn(styles.virtualRow, (_a = {},
                    _a[styles.highlighted] = highlightedIndex === virtualRow.index,
                    _a)), style: {
                    transform: "translateY(".concat(virtualRow.start, "px)"),
                } },
                renderGroup(),
                !isGroup(option) && (React.createElement(Option, __assign({}, getOptionProps(option, virtualRow.index))))));
        });
    };
    var contentNodeProps = {
        className: styles.inner,
        style: { height: "".concat(rowVirtualizer.totalSize, "px") },
        ref: listRef,
    };
    var renderWithCustomScrollbar = function () { return (React.createElement(Scrollbar, { className: styles.scrollable, ref: scrollbarRef, horizontalAutoStretch: optionsListWidth === 'content', scrollableNodeProps: { onScroll: onScroll, ref: parentRef }, contentNodeProps: contentNodeProps }, renderList())); };
    var renderWithNativeScrollbar = function () {
        if (visibleOptions) {
            return (React.createElement("div", { className: styles.scrollable, ref: mergeRefs([parentRef, ref]), onScroll: onScroll },
                React.createElement("div", __assign({}, contentNodeProps), renderList())));
        }
        return React.createElement("div", __assign({}, contentNodeProps), renderList());
    };
    var resetHighlightedIndex = function () { return setHighlightedIndex === null || setHighlightedIndex === void 0 ? void 0 : setHighlightedIndex(-1); };
    if (options.length === 0 && !emptyPlaceholder) {
        return null;
    }
    return (React.createElement("div", { className: cn(styles.virtualOptionsList, styles[SIZE_TO_CLASSNAME_MAP[size]], className), "data-test-id": dataTestId },
        header && (React.createElement("div", { className: styles.virtualOptionsListHeader, onMouseEnter: resetHighlightedIndex }, header)),
        nativeScrollbar ? renderWithNativeScrollbar() : renderWithCustomScrollbar(),
        emptyPlaceholder && options.length === 0 && (React.createElement("div", { className: styles.emptyPlaceholder }, emptyPlaceholder)),
        showFooter && footer && (React.createElement("div", { onMouseEnter: resetHighlightedIndex, className: cn(styles.virtualOptionsListFooter, (_b = {},
                _b[styles.withBorder] = visibleOptions && flatOptions.length > visibleOptions,
                _b)) }, footer))));
});

export { VirtualOptionsList };
